import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeGetItem = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSetItem = (key, value) => {
  try { localStorage.setItem(key, value); } catch {}
};
const safeRemoveItem = (key) => {
  try { localStorage.removeItem(key); } catch {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(safeGetItem('ff_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      safeRemoveItem('ff_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, mfaCode = null) => {
    const payload = { email, password };
    if (mfaCode) payload.mfa_code = mfaCode;
    const response = await axios.post(`${API}/auth/login`, payload);
    const data = response.data;

    // MFA required — return indicator for the UI
    if (data.mfa_required) {
      return { mfa_required: true };
    }

    const { token: newToken, user: userData } = data;
    safeSetItem('ff_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (name, email, password, attribution = {}) => {
    const response = await axios.post(`${API}/auth/signup`, {
      name,
      email,
      password,
      // Acquisition attribution — Signup.js reads UTM/referrer from the
      // visitor's first-touch session before submitting so we can group
      // Pro upgrades by acquisition channel in the admin analytics tab.
      signup_utm_source:   attribution.utm_source   || null,
      signup_utm_medium:   attribution.utm_medium   || null,
      signup_utm_campaign: attribution.utm_campaign || null,
      signup_utm_term:     attribution.utm_term     || null,
      signup_utm_content:  attribution.utm_content  || null,
      signup_referrer:     attribution.referrer     || null,
    });
    return response.data;
  };

  const loginWithToken = (newToken, userData) => {
    safeSetItem('ff_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    safeRemoveItem('ff_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    signup,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isPaid: user?.plan === 'paid'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
